// website/components/ResultsLabInspector.jsx
// Sort Key Inspector — the diagnostic half of the Results Lab.
// Renders every sorted item (full list, not paginated) with the computed
// sort key for the active sortBy mode, so we can eyeball ordering in seconds
// and spot ties / inversions the verify button flags. Also shows the paginated
// slice so we can confirm pagination lines up with the full ordering.

import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { cleanPerBookEv } from '../utils/canonicalEv';
import { normBookName } from '../../utils/resultSorting';
import { describeItem, HANDLED_SORT_MODES } from '../utils/resultsLabVerify';

function parseDateMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'object') {
    if (typeof v._seconds === 'number') return v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6);
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (v instanceof Date) return v.getTime();
    return null;
  }
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function bestPrice(item) {
  const raw = item?.raw || item;
  const sgpPrice = item?.combined_price ?? raw?.combined_price;
  if (sgpPrice != null && !isNaN(sgpPrice)) return sgpPrice;
  const bookOdds = item?.bookOdds || raw?.bookOdds || {};
  const prices = Object.values(bookOdds)
    .map(o => (typeof o === 'number' ? o : o?.price ?? null))
    .filter(p => p !== null && !isNaN(p));
  if (prices.length > 0) return Math.max(...prices);
  return raw?.price ?? raw?.american_price ?? raw?.odds ?? null;
}

function bookCount(item) {
  const bo = item?.bookOdds || item?.raw?.bookOdds || {};
  return Object.keys(bo).length || item?.availableBooks?.length || item?.raw?._sportsbook_count || 0;
}

function dateMs(item) {
  const raw = item?.raw || item;
  const rawDate = item?.date || raw.start_date || raw.commence_time || raw.event_date || raw.game_time || raw.start_time || raw._start_date;
  return parseDateMs(rawDate);
}

function marketName(item) {
  return (item?.raw?.market || item?.raw?.market_id || item?.title || '').toLowerCase();
}

function evSortKey(item, consensusOptions) {
  const raw = item?.raw || item || {};
  const isSGP = raw.ev_method === 'individual_leg_devig';
  const bookOdds = raw.bookOdds || item?.bookOdds || {};
  const evSrc = raw._ev_source || raw.devig_methods?.ev_source;
  if (evSrc === 'samebook') {
    const serverEv = raw.ev_percent_clean ?? raw.ev_percent ?? null;
    if (Number.isFinite(serverEv)) return serverEv;
  }
  let best = null;
  for (const [bk, bd] of Object.entries(bookOdds)) {
    const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
    if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
  }
  if (best != null) return best;
  const serverEv = raw.ev_percent_clean ?? raw.ev_percent ?? null;
  return Number.isFinite(serverEv) ? serverEv : null;
}

function myBooksEvSortKey(item, myBooks, consensusOptions) {
  const raw = item?.raw || {};
  const isSGP = raw.ev_method === 'individual_leg_devig';
  const bookOdds = raw.bookOdds || item?.bookOdds || {};
  const myBooksNorm = new Set((myBooks || []).map(b => normBookName(b)));
  let best = null;
  for (const [bk, bd] of Object.entries(bookOdds)) {
    if (!myBooksNorm.has(normBookName(bk))) continue;
    const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
    if (wc != null && (best == null || wc > best)) best = wc;
  }
  return best;
}

function keyFor(item, sortBy, myBooks, consensusOptions) {
  switch (sortBy) {
    case 'ev':          return evSortKey(item, consensusOptions);
    case 'price_desc':
    case 'price_asc':   return bestPrice(item);
    case 'date':        return dateMs(item);
    case 'sportsbooks': return bookCount(item);
    case 'market':      return marketName(item);
    case 'mybooks':     return myBooksEvSortKey(item, myBooks, consensusOptions);
    default:            return null;
  }
}

function fmtKey(k, sortBy) {
  if (k == null) return 'null';
  if (sortBy === 'date') {
    try { return new Date(k).toISOString().replace('T', ' ').slice(0, 16); } catch { return String(k); }
  }
  if (sortBy === 'market') return String(k);
  if (typeof k === 'number') return k.toFixed(2);
  return String(k);
}

export default function ResultsLabInspector({ sorted, paginated, sortBy, myBooks, consensusOptions }) {
  const rows = useMemo(() => {
    if (!sorted?.length) return [];
    return sorted.map((item, i) => {
      const key = keyFor(item, sortBy, myBooks, consensusOptions);
      const isOnPage = paginated?.some(p => p?.id === item?.id);
      return { i, item, key, isOnPage };
    });
  }, [sorted, paginated, sortBy, myBooks, consensusOptions]);

  const showKeys = HANDLED_SORT_MODES.has(sortBy) && sortBy !== 'relevance';

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Sort Key Inspector</Text>
          <Text style={styles.subtitle}>
            {sorted?.length || 0} items in final order · {showKeys ? `key = ${sortBy}` : `no key extractor for '${sortBy}' (shows raw order only)`}
          </Text>
        </View>
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={{ paddingBottom: 16 }}>
        {rows.length === 0 && (
          <Text style={styles.empty}>No items. Load a fixture to populate the inspector.</Text>
        )}
        {rows.map((r) => {
          const prev = r.i > 0 ? rows[r.i - 1] : null;
          let inversion = false;
          if (prev && showKeys && prev.key != null && r.key != null) {
            const isDesc = ['ev', 'price_desc', 'sportsbooks', 'mybooks'].includes(sortBy);
            inversion = isDesc ? prev.key < r.key : prev.key > r.key;
          }
          return (
            <View key={r.item?.id || r.i} style={[styles.row, inversion && styles.rowInversion, r.isOnPage && styles.rowOnPage]}>
              <Text style={styles.idx}>#{r.i}</Text>
              <Text style={styles.desc} numberOfLines={1}>{describeItem(r.item)}</Text>
              {showKeys ? (
                <Text style={[styles.key, inversion && styles.keyInversion]}>{fmtKey(r.key, sortBy)}</Text>
              ) : (
                <Text style={styles.keyMuted}>—</Text>
              )}
              {r.isOnPage && <Text style={styles.pageBadge}>PAGE</Text>}
              {inversion && (
                <Ionicons name="arrow-up" size={11} color="#f87171" style={{ marginLeft: 4 }} />
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', borderRadius: 10, backgroundColor: 'rgba(0,0,0,0.25)', maxHeight: 500, flex: 1 },
  header: { paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)' },
  title: { color: '#fff', fontSize: 14, fontWeight: '700' },
  subtitle: { color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 },
  scroll: { flex: 1 },
  empty: { color: 'rgba(255,255,255,0.4)', fontSize: 12, padding: 16, textAlign: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)' },
  rowInversion: { backgroundColor: 'rgba(248,113,113,0.08)' },
  rowOnPage: { borderLeftWidth: 2, borderLeftColor: '#A259FF' },
  idx: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '600', width: 36 },
  desc: { color: 'rgba(255,255,255,0.85)', fontSize: 12, flex: 1 },
  key: { color: '#60a5fa', fontSize: 11, fontWeight: '700', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, minWidth: 90, textAlign: 'right' },
  keyInversion: { color: '#f87171' },
  keyMuted: { color: 'rgba(255,255,255,0.3)', fontSize: 11, minWidth: 90, textAlign: 'right' },
  pageBadge: { color: '#A259FF', fontSize: 9, fontWeight: '700', backgroundColor: 'rgba(162,89,255,0.15)', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 3, overflow: 'hidden' },
});
